import forEach from 'lodash/forEach';
import { rgba } from 'polished';
import SvgPath from 'svg-path-to-canvas';
import EventEmitter from 'wolfy87-eventemitter';
import last from 'lodash/last';
import { inject, injectable } from 'inversify';
import CSVGFaster from '../../csvg-faster';
import { renderText } from '../../../helpers/canvas';
import { isSvgPath } from '../../../helpers/svg';
import { ICategory } from '../../../types/category';
import api from '../../../services/api';
import type { IBackgroundController, IBackgroundEvents } from './background.interface';
import { BACKGROUND_MODULE_IDS } from './background.types';
import { ZOOM_MODULE_IDS } from '../zoom/zoom.types';
import type { IZoomController } from '../zoom/zoom.interface';

@injectable()
export class BackgroundController extends EventEmitter implements IBackgroundController {
  ctx: CanvasRenderingContext2D;

  schemeBackground: string;

  failed: boolean = false;

  loaded: boolean = false;

  outlines: object;

  imageBackground: HTMLImageElement;

  imageBackgoundLowResolution: HTMLCanvasElement = document.createElement('canvas');

  imageBackgoundLowResolutionHeight: number;
  imageBackgoundLowResolutionWidth: number;

  svgBackground: CSVGFaster;

  useImage: boolean = false;

  @inject(BACKGROUND_MODULE_IDS.BACKGROUND_EVENTS) public events: IBackgroundEvents;
  @inject(ZOOM_MODULE_IDS.ZOOM_CONTROLLER) public zoomController: IZoomController;

  getbackgroundSrc() {
    const { lang } = window.Widget.settings;
    const { hallLayoutMeta } = window.Widget.schemeParameters;
    const bg =
      !lang || lang === 'ru'
        ? hallLayoutMeta.bg
        : hallLayoutMeta[`bg_${lang}`] || hallLayoutMeta.bg;

    if (!bg) {
      throw Error('no background');
    }

    return bg;
  }

  preloadImageBackground = async () => {
    this.useImage = true;
    return new Promise((resolve, reject) => {
      this.imageBackground = new Image();
      const backgroundSrc = this.getbackgroundSrc();
      this.imageBackground.src = backgroundSrc;

      this.imageBackground.onload = (event) => {
        this.createCachedBackground();
        resolve(event.target);
      };

      this.imageBackground.onerror = () => {
        reject();
      };
    });
  };

  createSVGBackground = async () => {
    const backgroundSrc = this.getbackgroundSrc();

    const { data } = await api.get(backgroundSrc);

    this.svgBackground = new CSVGFaster(data);

    return this.svgBackground;
  };

  getImageSize = () => {
    const { width, height } = window.Widget.schemeParameters.hallLayoutMeta;

    let imgWidth = width;
    let imgHeight = height;

    const screenWidth = window.Widget.width;
    const screenHeight = window.Widget.height;
    const scaleX = screenWidth / imgWidth;
    const scaleY = screenHeight / imgHeight;
    let minScale = scaleX < scaleY ? scaleX : scaleY;

    if (minScale < 1) {
      imgHeight = imgHeight * minScale * window.Widget.dpr;
      imgWidth = imgWidth * minScale * window.Widget.dpr;
    }

    return {
      imgHeight: Math.round(imgHeight),
      imgWidth: Math.round(imgWidth),
    };
  };

  createCachedBackground = () => {
    const ctx = this.imageBackgoundLowResolution.getContext('2d');
    const { width, height } = window.Widget.schemeParameters.hallLayoutMeta;
    const { imgHeight, imgWidth } = this.getImageSize();

    this.imageBackgoundLowResolution.height = imgHeight;
    this.imageBackgoundLowResolution.width = imgWidth;
    this.imageBackgoundLowResolutionHeight = imgHeight;
    this.imageBackgoundLowResolutionWidth = imgWidth;

    ctx.drawImage(this.imageBackground, 0, 0, width, height, 0, 0, imgWidth, imgHeight);
  };

  setBackgroundCtx(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  renderOutlines(outlines) {
    if (!window.Widget.schemeSupported) return null;

    this.outlines = outlines;
  }

  render = () => {
    if (this.failed || !this.ctx) return;

    if (this.useImage) {
      this.renderImageBackground();
      return;
    }
    this.renderSVGBackground();
  };

  renderImageBackground = () => {
    const { zoomIsMinimum, zoomRange, currentCustomZoom } = this.zoomController;
    const maximumZoom: number = last(zoomRange);
    const showCacheScheme = currentCustomZoom
      ? // Switching the scheme when zooming more than 5%
        Math.floor((currentCustomZoom * 100) / maximumZoom) < 5
      : zoomIsMinimum;

    if (showCacheScheme) {
      const { width, height } = window.Widget.schemeParameters.hallLayoutMeta;

      return this.ctx.drawImage(
        this.imageBackgoundLowResolution,
        0,
        0,
        this.imageBackgoundLowResolutionWidth,
        this.imageBackgoundLowResolutionHeight,
        0,
        0,
        width,
        height,
      );
    }

    const { x, y, x2, y2 } = window.Widget.getViewport();
    const startX = x < 0 ? 0 : x;
    const startY = y < 0 ? 0 : y;

    return this.ctx.drawImage(this.imageBackground, startX, startY, x2, y2, startX, startY, x2, y2);
  };

  renderSVGBackground = () => {
    this.svgBackground.draw({
      ctx: this.ctx,
      offsetX: 0,
      offsetY: 0,
      scale: 1,
    });
  };

  init() {
    this.emit(this.events.INIT);
  }

  update() {
    this.emit(this.events.UPDATE);
  }

  forceUpdate() {
    this.emit(this.events.FORCE_UPDATE);
  }

  renderLeftovers = (sectors) => {
    if (!sectors) return;
    Object.keys(sectors).forEach((sectorName) => {
      const sector = sectors[sectorName];
      if (!sector.path || !isSvgPath(sector.path)) return;
      const sp = new SvgPath(sector.path);
      const [cx, cy] = sp.center;
      const height = sp.size[1];
      const sectorHeight = height * this.zoomController.currentZoom;
      const defaultFontSize = 16 * window.Widget.dpr;
      const fontSize =
        sectorHeight < defaultFontSize ? Math.round(sectorHeight) - 2 : defaultFontSize;

      sp.save()
        .beginPath()
        .translate(0, 0)
        .strokeStyle(sector.color)
        .fillStyle(rgba(sector.color, 0.2))
        .lineWidth(5)
        .lineJoin('round')
        .lineCap('round')
        .to(window.Widget.seatsCtx)
        .stroke()
        .fill();

      renderText({
        ctx: window.Widget.seatsCtx,
        text: sector.count,
        x: cx,
        y: cy,
        weight: 'bold',
        color: sector.color,
        fontSize: fontSize / this.zoomController.currentZoom,
      });
    });
  };

  renderCategoriesOutlines() {
    if (this.zoomController.zoomIsMaximum) return;

    forEach(window.Widget.categoriesController.categories, (category: ICategory) => {
      category.paths &&
        // @ts-ignore
        JSON.parse(category.paths).forEach((path) => {
          const sp = new SvgPath(path);
          const { pricesIndexes, selectedPrices } = window.Widget.pricesController;
          const opacity = selectedPrices.has(pricesIndexes[category.price]) ? 0.64 : 0.32;
          const stroke = category.count ? rgba(category.color, 0.96) : rgba('#999', 0.96);
          const fill = category.count ? rgba(category.color, opacity) : rgba('#999', 0.32);
          sp.save()
            .beginPath()
            .translate(0, 0)
            .strokeStyle(stroke)
            .fillStyle(fill)
            .lineWidth(5)
            .lineJoin('round')
            .lineCap('round')
            .to(window.Widget.seatsCtx)
            .stroke()
            .fill();
        });
    });
  }
}
